import { type ResultOf } from '@graphql-typed-document-node/core';
import { graphql } from '@mp/graphql/generated';
import { useCurrentUser } from '@mp/hooks';
import { useGraphQLQuery } from './useGraphQLRequest';

const ProfessionalPoolPqsGql = graphql(`
  query GetProfessionalPoolPqs($pool_id: Int!) {
    me {
      professional {
        id
        pool_pqs: professional_qualifications_for_pool(pool_id: $pool_id) {
          id
        }
      }
    }
  }
`);

const ProfessionalPoolOnboardingStatusGql = graphql(`
  query GetProfessionalPoolOnboardingStatus($pool_id: Int!) {
    me {
      professional {
        id
        pool_onboarding_status: pool_id_onboarding_status(pool_id: $pool_id)
      }
    }
  }
`);

type ProfessionalPoolPqsResult = ResultOf<typeof ProfessionalPoolPqsGql>;
type ProfessionalPoolOnboardingStatusResult = ResultOf<typeof ProfessionalPoolOnboardingStatusGql>;
export type ProfessionalPoolPq = NonNullable<
  NonNullable<NonNullable<ProfessionalPoolPqsResult['me']>['professional']>['pool_pqs']
>[number];

export type UseProfessionalPoolContextResult = {
  poolOnboardingStatus: string | null | undefined;
  poolPqs: ProfessionalPoolPq[];
  hasPoolPq: boolean;
  onboardingStatusUnavailable: boolean;
  isLoading: boolean;
  refetch: () => Promise<unknown>;
};

export type UseProfessionalPoolContextOptions = {
  refetchStatusOnFocus?: boolean;
};

export const useProfessionalPoolContext = (
  poolId: number | null | undefined,
  options: UseProfessionalPoolContextOptions = {},
): UseProfessionalPoolContextResult => {
  const { refetchStatusOnFocus = false } = options;
  const { currentUser } = useCurrentUser();
  const professionalId = currentUser?.professional?.id;
  const enabled = !!professionalId && poolId !== null && poolId !== undefined;

  const pqsQuery = useGraphQLQuery<ProfessionalPoolPqsResult>({
    enabled,
    operationName: 'GetProfessionalPoolPqs',
    query: ProfessionalPoolPqsGql,
    variables: {
      pool_id: poolId ?? 0,
    },
  });

  const poolPqs = pqsQuery.data?.me?.professional?.pool_pqs ?? [];
  const hasPoolPq = poolPqs.length > 0;

  const statusEnabled = enabled && hasPoolPq;
  const statusQuery = useGraphQLQuery<ProfessionalPoolOnboardingStatusResult>({
    enabled: statusEnabled,
    operationName: 'GetProfessionalPoolOnboardingStatus',
    query: ProfessionalPoolOnboardingStatusGql,
    variables: {
      pool_id: poolId ?? 0,
    },
    onError: () => undefined,
    refetchOnWindowFocus: refetchStatusOnFocus,
    ...(refetchStatusOnFocus ? { staleTime: 0 } : {}),
  });

  const pqsLoading = enabled ? pqsQuery.isLoading : pqsQuery.isInitialLoading;
  const statusLoading = statusEnabled ? statusQuery.isLoading : statusQuery.isInitialLoading;

  const refetch = async () => {
    await Promise.all([
      pqsQuery.refetch(),
      statusEnabled ? statusQuery.refetch() : Promise.resolve(),
    ]);
  };

  return {
    poolOnboardingStatus: statusQuery.data?.me?.professional?.pool_onboarding_status,
    poolPqs,
    hasPoolPq,
    onboardingStatusUnavailable: statusEnabled && statusQuery.isError,
    isLoading: pqsLoading || statusLoading,
    refetch,
  };
};
